import React, { FC } from 'react';
import { Box, CircularProgress, Fade, Link, Typography } from '@mui/material';
import StraightenIcon from '@mui/icons-material/Straighten';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { palette } from '../../theme';

export type MeasurementSystem = 'imperial' | 'metric';

export interface MeasurementStepProps {
  accentColor: string;
  isGuardian: boolean;
  measurementSystem: MeasurementSystem;
  isLoading: boolean;
  onMeasurementChange: (v: MeasurementSystem) => void;
  onBack: () => void;
  onNext: () => void;
}

interface OptionRowProps {
  label: string;
  sublabel: string;
  selected: boolean;
  accentColor: string;
  onClick: () => void;
}

const OptionRow: FC<OptionRowProps> = ({ label, sublabel, selected, accentColor, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: '12px 16px',
      borderRadius: 2,
      border: `1.5px solid ${selected ? accentColor : palette.grey[200]}`,
      backgroundColor: selected ? `${accentColor}0D` : '#fff',
      cursor: 'pointer',
      mb: 1.5,
      transition: 'all 0.15s ease',
      '&:hover': {
        borderColor: accentColor,
        backgroundColor: `${accentColor}0D`,
      },
    }}
  >
    {/* Radio dot */}
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: `2px solid ${selected ? accentColor : palette.grey[300]}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {selected && (
        <Box
          sx={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            backgroundColor: accentColor,
          }}
        />
      )}
    </Box>

    <Typography variant="body2" fontWeight={selected ? 600 : 400} color="text.primary">
      {label}{' '}
      <Typography component="span" variant="body2" color="text.secondary">
        {sublabel}
      </Typography>
    </Typography>
  </Box>
);

const MeasurementStep: FC<MeasurementStepProps> = ({
  accentColor,
  isGuardian,
  measurementSystem,
  isLoading,
  onMeasurementChange,
  onBack,
  onNext,
}) => {
  const subtitle = isGuardian
    ? "Choose your athlete's preferred units so their data is displayed correctly."
    : 'Choose your preferred units so data is displayed the way you like.';

  if (isLoading) {
    return (
      <Fade in>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 320,
            gap: 2,
          }}
        >
          <CircularProgress size={48} sx={{ color: accentColor }} />
          <Typography variant="body2" color="text.secondary">
            Saving your preference…
          </Typography>
        </Box>
      </Fade>
    );
  }

  return (
    <Box>
      {/* Back link */}
      <Link
        component="button"
        onClick={onBack}
        underline="none"
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
          fontSize: 14,
          mb: 3,
          '&:hover': { color: 'text.primary' },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 18, mr: 0.25 }} />
        Back
      </Link>

      {/* Icon badge */}
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2.5,
          backgroundColor: `${accentColor}18`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5,
        }}
      >
        <StraightenIcon sx={{ color: accentColor, fontSize: 26 }} />
      </Box>

      <Typography variant="h5" fontWeight={700} mb={0.5}>
        Measurement Preferences
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3} sx={{ lineHeight: 1.6 }}>
        {subtitle}
      </Typography>

      {/* Options */}
      <OptionRow
        label="Imperial"
        sublabel="(mi, ft, lb)"
        selected={measurementSystem === 'imperial'}
        accentColor={accentColor}
        onClick={() => onMeasurementChange('imperial')}
      />
      <OptionRow
        label="Metric"
        sublabel="(km, m, kg)"
        selected={measurementSystem === 'metric'}
        accentColor={accentColor}
        onClick={() => onMeasurementChange('metric')}
      />

      {/* Back / Next footer */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Link
          component="button"
          onClick={onBack}
          underline="none"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            fontSize: 14,
            '&:hover': { color: 'text.primary' },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 18, mr: 0.25 }} />
          Back
        </Link>

        <Link
          component="button"
          onClick={onNext}
          underline="none"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: accentColor,
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          Next
          <ChevronRightIcon sx={{ fontSize: 18, ml: 0.25 }} />
        </Link>
      </Box>
    </Box>
  );
};

export default MeasurementStep;
